import React, { useContext, useEffect } from 'react';

import UploadImage from '@/components/UploadImage';
import { PageOptionContext, PageOptionDispatchContext } from '@/components/dashboard/context';
import { DEFAULT_META_DESCRIPTION } from '@/constants';
import sharedStyles from '@/styles/Shared.module.css';
import { World } from '@styled-icons/boxicons-regular';
import { Text } from '@typedream/ui';
import classNames from 'classnames';
import Image from 'next/legacy/image';
import { PagesOptionFacebookProps } from './types';

// SEO Settings:
// title = facebook.title || metatags.title || space.name/page.name || LOGO_NAME
// description = facebook.description || metatags.description ||  DEFAULT_META_DESCRIPTION
// image = facebook.image

export function PagesOptionFacebook({
  columns,
  disabled = false,
  onRemoveCard,
}: PagesOptionFacebookProps) {
  const { page, meta, facebook, facebookImage } = useContext(PageOptionContext);
  const { setFacebook, setFacebookImage } = useContext(PageOptionDispatchContext);
  const imagePreview = React.useRef(null);

  useEffect(() => {
    if (facebookImage) {
      const reader = new FileReader();
      const { current } = imagePreview;
      current.file = facebookImage;
      reader.onload = (e) => {
        current.src = e.target.result;
      };
      reader.readAsDataURL(facebookImage);
    } else if (facebook.imageURL) {
      const { current } = imagePreview;
      current.src = facebook.imageURL;
    }
  }, [facebookImage]);

  return (
    <div className={classNames({ columns })}>
      <div className={classNames({ column: columns })}>
        <Text as="p" size="md" className="mb-2">
          Facebook Image
        </Text>
        <UploadImage
          setfile={setFacebookImage}
          file={facebookImage}
          containerWidth={100}
          containerHeight={100}
          width={100}
          height={100}
          borderRadius={0}
          className="object-cover mb-4"
          placeholderImage={facebook.imageURL || '/images/image-placeholder.png'}
          disabled={disabled}
          onRemoveCard={onRemoveCard}
        />
        <Text as="p" size="md" className="mb-2">
          Facebook Title
        </Text>
        <input
          className={classNames(sharedStyles.inputMedium, 'mb-4')}
          type="text"
          value={facebook.title || ''}
          placeholder={meta.title || page.name}
          onChange={(e) => setFacebook((curr) => ({ ...curr, title: e.target.value }))}
          disabled={disabled}
        />

        <Text as="p" size="md" className="mb-2">
          Facebook Description
        </Text>
        <textarea
          className={classNames(sharedStyles.inputMedium, '!h-[100px] mb-4')}
          value={facebook.description || ''}
          placeholder={meta.description || DEFAULT_META_DESCRIPTION}
          onChange={(e) => setFacebook((curr) => ({ ...curr, description: e.target.value }))}
          disabled={disabled}
        />
      </div>

      <div className={classNames({ column: columns })}>
        <Text as="p" size="xs" className="mb-2">
          Facebook Preview
        </Text>
        <div
          className="w-full, my-4 mx-0 py-4 px-5 rounded-lg bg-neutral-25"
          style={{ boxShadow: 'var(--shadow-modal)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Image src="/images/facebook.png" width={30} height={30} />
              <div className="ml-2">
                <Text as="p" size="xs" weight="medium" className="!leading-4 text-neutral-900">
                  Name
                </Text>
                <div className="inline-flex items-center py-0.5 px-1 gap-1 bg-[#e4e6eb] rounded-md">
                  <World size={10} className="text-neutral-900" />
                  <Text
                    as="p"
                    size="2xs"
                    weight="medium"
                    className="!text-[11px] !leading-[14px] text-neutral-900"
                  >
                    Public
                  </Text>
                </div>
              </div>
            </div>
            <Image src="/images/facebook.svg" width={23} height={23} />
          </div>
          <div className="bg-[#0000000d] rounded-full my-2 mx-0 h-2.5 w-full" />
          <div className="bg-[#0000000d] rounded-full my-2 mx-0 h-2.5 w-3/4" />
          <div className="mt-4 border border-solid border-[#0000000d] bg-[#f0f2f5] rounded-xl">
            {(facebookImage || facebook.imageURL) && (
              <img
                src={facebook.imageURL}
                ref={imagePreview}
                className="w-full h-[180px] object-cover rounded-t-xl"
                alt="Facebook"
              />
            )}
            <div className="pl-3 pr-3 pb-2 pt-2">
              <Text as="p" size="xs" className="text-[#00000066] leading-[13px]">
                WEBSITE.COM
              </Text>
              <Text as="p" size="md" weight="bold" className="text-neutral-900 mb-0">
                {facebook.title || meta.title || page.name}
              </Text>
              <Text as="p" size="xs" className="text-[#00000066] leading-[13px]">
                {facebook.description || meta.description || DEFAULT_META_DESCRIPTION}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
